import { Controller } from 'stimulus'
import { parseUnit } from 'src/utils/units_format'

const DEFAULT_RENDER_OPTIONS = Object.freeze({
  viewMode: 'default',
  shapeOnly: false,
})
const RENDER_ANNOTATION_KEYS = Object.freeze([
  'overall_dimensions',
  'load_details',
  'oversize_exceptions',
  'deck_fit_dimensions',
  'permit_axle_details',
  'axle_weight_panels',
])
const LOAD_FIELD_SELECTOR = [
  '[data-v2-load-field]',
  '[data-v2-load-extra-field]',
  '#quote_commodity_description',
  '#quote_commodity_length',
  '#quote_commodity_support_length',
  '#quote_commodity_support_offset',
  '#quote_commodity_width',
  '#quote_commodity_height',
  '#quote_commodity_weight',
].join(', ')
const EXTRA_LOAD_ROW_SELECTOR = '[data-behavior~="v2-load-dimensions-extra-row"]'
const SELECTED_LOAD_ASSIGNMENT_SELECTOR = '[data-behavior~="v2-selected-load-assignment-id"]'
const LOAD_LIST_TOGGLE_SELECTOR = '[data-behavior~="v2-load-list-toggle"]'

const LIVE_PREVIEW_DEBOUNCE_MS = 350

// Plain-language endpoints for each focusable dimension field, shown under the
// 3D preview while the field has focus so the highlighted bracket is
// unambiguous even in words.
const MEASUREMENT_DESCRIPTIONS = Object.freeze({
  front_connection_to_first_axle: 'kingpin center → center of the first trailer axle',
  front_connection_to_trailer_front: 'front edge of the trailer → kingpin center',
  trailer_length: 'front edge of the trailer → rear edge of the trailer',
  tractor_length: 'front bumper → rear of the tractor frame',
  neck_length: 'front edge of the trailer → end of the neck',
  well_length: 'start of the well → end of the well',
  upper_deck_length: 'front edge of the trailer → end of the upper deck',
  deck_height: 'ground → top of the main loading deck',
  lower_deck_height: 'ground → top of the lower deck',
  well_deck_height: 'ground → top of the well deck',
  rear_deck_height: 'ground → top of the rear deck',
})

// The load-only preview has no trailer decks, so a shorter support footprint
// can't derive its pedestal height from deck clearance the way the server-side
// scenes do. Use a representative height instead — cargo bodies with shorter
// supports overhang rear/upper decks a couple of feet up — capped by ratio so
// short loads keep a readable body above the pedestal. Keep in sync with the
// visual pedestal constants in LoadPlanning::LoadVisualGeometry.
const LOAD_ONLY_SUPPORT_PEDESTAL_MAX_IN = 30
const LOAD_ONLY_SUPPORT_PEDESTAL_HEIGHT_RATIO = 0.2

function stringValue(value) {
  return value === null || value === undefined ? '' : String(value)
}

function humanizeFieldName(field) {
  return stringValue(field).replace(/_/g, ' ').trim()
}

// Pure, exported for unit testing. Turns a backend render_reason code into a
// short human phrase for the readiness strip and live placeholder.
export function humanizeRenderReason(reason) {
  const code = stringValue(reason).trim()
  if (!code) return ''

  switch (code) {
    case 'invalid_trailer_deck_height':
      return 'needs deck height'
    case 'invalid_trailer_geometry':
      return 'check trailer geometry values'
    case 'invalid_trailer_length':
      return 'needs trailer length'
    default:
      break
  }

  if (code.indexOf('unsupported') === 0) return "this type can't be rendered yet"

  return code.replace(/_/g, ' ')
}

// Pull the human message out of a structured render_reason_detail. Each detail
// is a string-keyed hash { field, label, message } produced by the builder.
function detailMessage(detail) {
  return detail && typeof detail === 'object' ? stringValue(detail.message).trim() : ''
}

// Pure, exported for unit testing. Derives the readiness strip model from a
// render scene (+ optional paused reason / structured paused details). Keys off
// missing_geometry_fields, the spec-verified string array on
// render_scene.source_confidence. When pausedDetails carries structured
// messages they win over the coarse humanized reason; shape:
//   deriveReadiness(scene, { pausedReason: 'invalid_trailer_geometry',
//                            pausedDetails: [{ field, label, message }] })
export function deriveReadiness(scene, options = {}) {
  const messages = (options.pausedDetails || []).map(detailMessage).filter(Boolean)
  if (options.pausedReason || messages.length) {
    return {
      chips: [{ kind: 'error', label: 'Preview paused' }],
      planning: null,
      paused: {
        label: 'Showing the last valid preview. Correct the highlighted fields to update it.',
        messages: messages.length ? messages : [humanizeRenderReason(options.pausedReason)],
      },
    }
  }
  const sourceConfidence = (scene && scene.source_confidence) || {}
  const serverReadiness = options.readiness && typeof options.readiness === 'object'
    ? options.readiness
    : null
  const missingFields = serverReadiness && Array.isArray(serverReadiness.missing_fields)
    ? serverReadiness.missing_fields
    : Array.isArray(sourceConfidence.missing_geometry_fields)
      ? sourceConfidence.missing_geometry_fields
      : []
  const spacingsMissing = missingFields.indexOf('chassis_spacings') !== -1

  const chips = [{ kind: 'ready', label: 'Renders' }]
  if (serverReadiness) {
    chips.push(serverReadiness.render_exact === true
      ? { kind: 'ready', label: '3D geometry exact' }
      : { kind: 'assumed', label: '3D geometry uses defaults' })
  }
  missingFields.forEach(field => {
    if (field === 'chassis_spacings') {
      // Covers both the per-axle default (5') and the inter-unit default (8'),
      // so the chip names the fact without pinning a single number.
      chips.push({ kind: 'assumed', label: 'Axle spacings assumed' })
    } else if (field === 'unit_empty_weights') {
      chips.push({ kind: 'assumed', label: 'Unit empty weights missing' })
    } else if (field === 'axle_tire_configuration') {
      chips.push({ kind: 'assumed', label: 'Axle tire data missing' })
    } else if (field === 'planning_topology') {
      chips.push({ kind: 'assumed', label: 'Planning topology unsupported' })
    } else {
      chips.push({ kind: 'assumed', label: `${humanizeFieldName(field)} assumed` })
    }
  })

  let planning
  if (serverReadiness && serverReadiness.load_plannable !== true) {
    planning = {
      kind: 'warning',
      label: 'Load planning unavailable — complete axle positions and equipment data.',
    }
  } else if ((serverReadiness && serverReadiness.load_exact !== true) || spacingsMissing) {
    planning = {
      kind: 'warning',
      label: 'Load planning uses estimates — complete the assumed equipment fields.',
    }
  } else {
    planning = { kind: 'ready', label: 'Planner geometry complete — per-axle load calculation available.' }
  }

  return { chips, planning, paused: null }
}

export function parseLiveDimensionInches(rawValue, key, options = {}) {
  const value = stringValue(rawValue).trim().replace(/(^|[^\d.,])[.,](?=\d)/g, (_match, prefix) => `${prefix}0.`)
  if (!value || value.startsWith('-')) return null

  if (
    typeof window !== 'undefined' &&
    typeof window.parseDimensionInputInches !== 'function' &&
    typeof window.ensureDimensionInputParsing === 'function'
  ) {
    window.ensureDimensionInputParsing()
  }

  if (typeof window !== 'undefined' && typeof window.parseDimensionInputInches === 'function') {
    const parsedInches = window.parseDimensionInputInches(value, key)
    if (parsedInches === null || parsedInches === undefined || parsedInches === '') return null

    const inches = Number(parsedInches)
    return Number.isFinite(inches) && (inches > 0 || (options.allowZero && inches === 0)) ? inches : null
  }

  const feet = parseUnit(value)
  return Number.isFinite(feet) && (feet > 0 || (options.allowZero && feet === 0)) ? feet * 12 : null
}

export function parseLiveWeightPounds(rawValue) {
  const value = stringValue(rawValue).trim()
  if (!value || value.startsWith('-')) return null

  if (
    typeof window !== 'undefined' &&
    typeof window.parseWeightInputLb !== 'function' &&
    typeof window.ensureWeightInputParsing === 'function'
  ) {
    window.ensureWeightInputParsing()
  }

  if (typeof window !== 'undefined' && typeof window.parseWeightInputLb === 'function') {
    const pounds = Number(window.parseWeightInputLb(value))
    return Number.isFinite(pounds) && pounds > 0 ? pounds : null
  }

  const normalizedValue = value.toLowerCase()
  const multiplier = normalizedValue.match(/(kg|kilo|kilgram|kilogramm|killogramm|kilogram)/)
    ? 2.2046226218
    : normalizedValue.match(/(tons?|tonnes?|metric\s*tons?|mt\b|\bt\b)/)
      ? normalizedValue.match(/tonnes?|metric\s*tons?|mt\b|\bt\b/) ? 2204.6226218 : 2000
      : 1
  const numeric = Number(normalizedValue.replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric * multiplier) : null
}

export default class extends Controller {
  static targets = ['payload', 'container', 'stretchControl', 'stretchMeta', 'loadRow', 'loadListToggle', 'status', 'familyExamples', 'measurementCaption']

  connect() {
    this.connected = true
    this.currentAnnotationSettings = this.annotationSettings
    this.annotationSettingsRevision = 0
    this.annotationSettingsDirty = false
    if (this.loadPreview) {
      this.connectLoadPreview()
      return
    }

    this.currentStretchMode = 'closed'
    this.hasRenderedScene = false
    // These counters must remain monotonic across Stimulus reconnects. An
    // earlier connection can still be unwinding an awaited chunk import; if
    // sequence numbers are reused, that stale continuation can look current.
    this.livePreviewSequence = (this.livePreviewSequence || 0) + 1
    this.highlightedInputs = []
    this.focusedMeasurementKey = ''
    this.focusedMeasurementInput = null
    this.exampleActive = false
    this.exampleRenderSequence = (this.exampleRenderSequence || 0) + 1
    // Keep this monotonic across Stimulus reconnects so a chunk import started
    // by an earlier connection cannot become current again after reconnect.
    this.panelCreationGeneration = (this.panelCreationGeneration || 0) + 1
    this.panelCreationPromise = null
    this.bindFormStretchControls()
    this.bindRenderStretchControls()
    this.mount()
    this.connectLivePreview()
  }

  disconnect() {
    this.connected = false
    window.clearTimeout(this.annotationSaveTimer)
    this.annotationSaveTimer = null
    if (this.annotationSettingsDirty && this.annotationSettingsUrl) {
      if (this.annotationSaveAbortController) this.annotationSaveAbortController.abort()
      void this.saveAnnotationSettings({
        revision: this.annotationSettingsRevision,
        settings: { ...this.currentAnnotationSettings },
        keepalive: true,
      })
    }
    if (this.loadPreview) {
      this.disconnectLoadPreview()
      return
    }

    this.disconnectLivePreview()
    this.unbindRenderStretchControls()
    this.unbindFormStretchControls()
    this.destroyPanel()
    this.trailer3d = null
  }

  async mount() {
    const payload = this.renderPayload
    const panelGeneration = this.panelCreationGeneration

    if (!payload) {
      this.showInitialPlaceholder('render_payload_missing')
      return
    }

    if (payload.renderable !== true || !payload.render_scene) {
      const showingExample = this.livePreviewEnabled && (await this.showFamilyExample())
      // A type switch or Stimulus reconnect can invalidate this mount while it
      // waits for the example chunk. Its fallback must not clear the new mount.
      if (!this.connected || panelGeneration !== this.panelCreationGeneration) return
      if (!showingExample) {
        this.showInitialPlaceholder(payload.render_reason || 'render_unavailable')
      }
      return
    }

    try {
      const panel = await this.ensurePanel()
      if (!this.connected || panelGeneration !== this.panelCreationGeneration) return
      if (!panel) throw new Error('render_mount_failed')

      const renderOverrides = payload.render_overrides || payload.renderOverrides || null
      panel.update({
        trailer: payload.render_scene,
        stretchMode: 'closed',
        renderOptions: this.currentRenderOptions(),
        renderOverrides,
        annotationSettings: this.currentAnnotationSettings,
        annotationData: payload.render_annotations || null,
        allowDefaultLegalEnvelope: this.allowDefaultLegalEnvelope,
      })
      this.hasRenderedScene = true
      this.exampleActive = false
      this.updateMeasurementCaption()
      this.syncStretchControlsWithForm()
      if (this.livePreviewEnabled) {
        this.lastReadiness = payload.readiness || null
        this.renderReadiness(payload.render_scene, { readiness: this.lastReadiness })
      }
    } catch (_error) {
      this.showPlaceholder('Check the equipment dimensions and change a field to retry.', '3D preview unavailable')
      this.showReadinessMessage('Preview unavailable. The equipment has not rendered successfully.')
    }
  }

  // In live mode the initial not-yet-computed state should read as an
  // invitation rather than an internal reason code.
  showInitialPlaceholder(reason) {
    if (this.livePreviewEnabled) {
      this.showPlaceholder(
        'Renders as you type — choose a trailer type and enter the key dimensions.',
        '3D preview'
      )
      return
    }

    this.showPlaceholder(reason)
  }

  currentRenderOptions() {
    return {
      ...DEFAULT_RENDER_OPTIONS,
      focusedMeasurement: this.focusedMeasurementKey || '',
      focusedMeasurementValueIn: this.focusedMeasurementValueIn(),
    }
  }

  focusedMeasurementValueIn() {
    if (!this.focusedMeasurementInput) return undefined

    // The scene may still be a catalog example or the last valid preview.
    // Its geometry locates the bracket; the current field supplies its value.
    return parseLiveDimensionInches(this.focusedMeasurementInput.value, this.focusedMeasurementKey, {
      allowZero: this.focusedMeasurementKey === 'front_connection_to_trailer_front',
    })
  }

  currentUnitType() {
    const form = this.liveFormElement || this.element.closest('form')
    const select = form && form.querySelector('[data-behavior~="unit-type-select"]')
    return select ? stringValue(select.value).trim() : ''
  }

  familyExampleFor(family) {
    const examples = this.familyExampleScenes
    return (examples && family && examples[family]) || null
  }

  // Renders the canonical catalog example for the currently selected trailer
  // family — the labeled stand-in scene shown until the user's own values are
  // complete enough to render. Returns whether an example took the canvas.
  async showFamilyExample() {
    // Increment before looking up the example so switching to the blank option
    // also invalidates a family render that is still waiting on the 3D chunk.
    const sequence = (this.exampleRenderSequence += 1)
    const example = this.familyExampleFor(this.currentUnitType())
    if (!(example && example.scene)) {
      this.exampleActive = false
      this.updateMeasurementCaption()
      return false
    }

    const panel = await this.ensurePanel()
    if (!this.connected) return false
    // A real scene (or a newer example request) can land while the renderer
    // chunk import resolves; the stale example must not overwrite it.
    if (sequence !== this.exampleRenderSequence || this.hasRenderedScene) return true
    if (!panel) return false

    panel.update({
      trailer: example.scene,
      stretchMode: 'closed',
      renderOptions: this.currentRenderOptions(),
      // An explicitly empty load list keeps the example an unloaded rig —
      // absent overrides would fall back to the catalog's generic preview
      // load, which reads as cargo the user never entered.
      renderOverrides: { load_previews: [] },
      allowDefaultLegalEnvelope: false,
    })
    this.exampleActive = true
    this.updateMeasurementCaption()
    return true
  }

  setFocusedMeasurement(key, input = null) {
    this.focusedMeasurementKey = key || ''
    this.focusedMeasurementInput = input
    this.syncFocusedMeasurement()
    this.updateMeasurementCaption()
  }

  syncFocusedMeasurement() {
    if (this.panel && typeof this.panel.setFocusedMeasurement === 'function') {
      this.panel.setFocusedMeasurement(this.focusedMeasurementKey || '', this.focusedMeasurementValueIn())
    }
  }

  updateMeasurementCaption() {
    if (!this.hasMeasurementCaptionTarget) return

    const lines = []
    if (this.exampleActive) {
      const example = this.familyExampleFor(this.currentUnitType())
      if (example && example.label) {
        const exampleLine = document.createElement('div')
        exampleLine.className = 'ttRenderMeasurementCaption__example'
        exampleLine.textContent = example.label
        lines.push(exampleLine)
      }
    }

    const description = MEASUREMENT_DESCRIPTIONS[this.focusedMeasurementKey]
    if (description) {
      const focusLine = document.createElement('div')
      focusLine.className = 'ttRenderMeasurementCaption__focus'
      focusLine.textContent = `Showing: ${description}`
      lines.push(focusLine)
    }

    this.measurementCaptionTarget.replaceChildren(...lines)
    this.measurementCaptionTarget.hidden = lines.length === 0
  }

  destroyPanel() {
    // Dynamic imports cannot be cancelled, so invalidate any in-flight panel
    // creation before dropping its shared promise. Its continuation will see
    // the generation mismatch and must not recreate a destroyed canvas.
    this.panelCreationGeneration = (this.panelCreationGeneration || 0) + 1
    this.panelCreationPromise = null
    if (!this.panel) return

    this.panel.destroy()
    this.panel = null
  }

  navigateCamera(event) {
    if (event) event.preventDefault()
    const action = event && event.currentTarget
      ? event.currentTarget.dataset.cameraAction
      : null

    if (action && this.panel && typeof this.panel.navigateView === 'function') {
      this.panel.navigateView(action)
    }
  }

  changeAnnotationSetting() {
    const inputs = Array.from(
      this.element.querySelectorAll('[data-behavior~="render-annotation-setting"]')
    )
    this.currentAnnotationSettings = RENDER_ANNOTATION_KEYS.reduce((settings, key) => {
      const input = inputs.find(candidate => candidate.value === key)
      settings[key] = Boolean(input && input.checked)
      return settings
    }, {})
    this.annotationSettingsRevision += 1
    this.annotationSettingsDirty = true

    if (this.panel && typeof this.panel.setAnnotationSettings === 'function') {
      this.panel.setAnnotationSettings(this.currentAnnotationSettings)
    }

    this.scheduleAnnotationSettingsSave()
  }

  scheduleAnnotationSettingsSave() {
    if (!this.annotationSettingsUrl) return

    window.clearTimeout(this.annotationSaveTimer)
    if (this.annotationSaveAbortController) this.annotationSaveAbortController.abort()
    const revision = this.annotationSettingsRevision
    const settings = { ...this.currentAnnotationSettings }
    this.setAnnotationSaveStatus('Saving…')
    this.annotationSaveTimer = window.setTimeout(() => {
      this.annotationSaveTimer = null
      void this.saveAnnotationSettings({ revision, settings })
    }, 250)
  }

  async saveAnnotationSettings({
    revision = this.annotationSettingsRevision,
    settings = { ...this.currentAnnotationSettings },
    keepalive = false,
  } = {}) {
    if (!this.annotationSettingsUrl) return

    if (this.annotationSaveAbortController) this.annotationSaveAbortController.abort()
    const abortController = new AbortController()
    this.annotationSaveAbortController = abortController

    let response
    try {
      response = await fetch(this.annotationSettingsUrl, {
        method: 'PATCH',
        credentials: 'same-origin',
        signal: abortController.signal,
        keepalive,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
        body: JSON.stringify({
          tractor_trailer_render_settings: settings,
        }),
      })
    } catch (error) {
      if (error && error.name === 'AbortError') return
      if (revision === this.annotationSettingsRevision) {
        this.setAnnotationSaveStatus('Could not save')
      }
      return
    }

    if (this.annotationSaveAbortController === abortController) {
      this.annotationSaveAbortController = null
    }
    if (revision !== this.annotationSettingsRevision) return

    if (!response.ok) {
      this.setAnnotationSaveStatus('Could not save')
      return
    }

    try {
      const payload = await response.json()
      if (payload && payload.settings) {
        this.currentAnnotationSettings = this.normalizeAnnotationSettings(payload.settings)
        this.syncAnnotationSettingInputs()
        if (this.panel && typeof this.panel.setAnnotationSettings === 'function') {
          this.panel.setAnnotationSettings(this.currentAnnotationSettings)
        }
      }
    } catch (_error) {
      // A successful empty response still means the preference was persisted.
    }

    this.annotationSettingsDirty = false
    this.setAnnotationSaveStatus('Saved')
  }

  syncAnnotationSettingInputs() {
    this.element.querySelectorAll('[data-behavior~="render-annotation-setting"]').forEach(input => {
      input.checked = this.currentAnnotationSettings[input.value] === true
    })
  }

  setAnnotationSaveStatus(message) {
    const status = this.element.querySelector('[data-behavior~="render-annotation-save-status"]')
    if (!status) return

    status.textContent = message
    if (message === 'Could not save') {
      status.dataset.state = 'error'
    } else if (message === 'Saved') {
      status.dataset.state = 'saved'
    } else {
      delete status.dataset.state
    }
  }

  normalizeAnnotationSettings(value) {
    const supplied = value && typeof value === 'object' ? value : {}
    return RENDER_ANNOTATION_KEYS.reduce((settings, key) => {
      settings[key] = supplied[key] === true || supplied[key] === 'true'
      return settings
    }, {})
  }

  // Toggles the paused class on the mount so the last rendered frame is visibly
  // dimmed while the current inputs are not renderable.
  setPaused(paused) {
    this.element.classList.toggle('ttRenderMount--paused', paused === true)
  }

  showPlaceholder(reason, titleText = '3D preview unavailable') {
    this.destroyPanel()
    this.syncStretchControlDisplay(false)

    const wrapper = document.createElement('div')
    wrapper.className = 'ttRenderFallback'

    const body = document.createElement('div')
    body.className = 'ttRenderFallbackBody'

    const title = document.createElement('strong')
    title.textContent = titleText

    const detail = document.createElement('div')
    detail.textContent = String(reason || 'render_unavailable')

    body.appendChild(title)
    body.appendChild(detail)
    wrapper.appendChild(body)

    this.containerTarget.replaceChildren(wrapper)
  }

  connectLivePreview() {
    if (!this.livePreviewEnabled) return

    this.liveFormElement = this.element.closest('form')
    if (!this.liveFormElement) return

    this.handleLiveInput = () => {
      this.syncFocusedMeasurement()
      this.showReadinessMessage('Updating preview…')
      this.setPaused(this.hasRenderedScene)
      window.clearTimeout(this.liveDebounceTimer)
      this.liveDebounceTimer = window.setTimeout(() => this.requestLivePreview(), LIVE_PREVIEW_DEBOUNCE_MS)
    }
    this.handleLiveChange = event => {
      // A trailer type switch invalidates the current render entirely — a stale
      // wrong-type frame must never persist across it. Clear immediately; the
      // fetch below re-renders once the new type's geometry is valid.
      if (this.isUnitTypeChange(event)) this.clearLiveRenderForTypeSwitch()

      this.syncFocusedMeasurement()
      this.showReadinessMessage('Updating preview…')
      this.setPaused(this.hasRenderedScene)
      window.clearTimeout(this.liveDebounceTimer)
      // Document-level delegated handlers (axle-spacing row sync, geometry
      // toggles) run after this form-level listener; defer one tick so the
      // FormData snapshot sees the updated rows instead of the previous state.
      this.liveDebounceTimer = window.setTimeout(() => this.requestLivePreview(), 0)
    }

    this.handleMeasurementFocusIn = event => {
      const key = event.target && event.target.dataset ? event.target.dataset.measurementKey : null
      if (key) this.setFocusedMeasurement(key, event.target)
    }
    this.handleMeasurementFocusOut = event => {
      const key = event.target && event.target.dataset ? event.target.dataset.measurementKey : null
      if (key) this.setFocusedMeasurement('')
    }

    this.liveFormElement.addEventListener('input', this.handleLiveInput)
    this.liveFormElement.addEventListener('change', this.handleLiveChange)
    this.liveFormElement.addEventListener('focusin', this.handleMeasurementFocusIn)
    this.liveFormElement.addEventListener('focusout', this.handleMeasurementFocusOut)
  }

  isUnitTypeChange(event) {
    const target = event && event.target
    return !!(
      target &&
      (target.name === 'tractor_trailer[unit_type]' || target.name === 'units[][unit_type]')
    )
  }

  clearLiveRenderForTypeSwitch() {
    if (this.liveAbortController) this.liveAbortController.abort()
    this.liveAbortController = null
    this.livePreviewSequence += 1
    this.destroyPanel()
    this.hasRenderedScene = false
    this.liveScene = null
    this.lastRenderedScene = null
    this.setPaused(false)
    this.clearFieldHighlights()
    this.clearReadiness()
    this.syncStretchControlsWithForm()
    this.exampleActive = false
    this.exampleRenderSequence += 1
    this.updateMeasurementCaption()
    const examplePromise = this.showFamilyExample()
    // Async functions run through their first await synchronously, so this is
    // the exact request sequence allocated by showFamilyExample above.
    const exampleSequence = this.exampleRenderSequence
    void examplePromise.then(shown => {
      if (exampleSequence !== this.exampleRenderSequence) return
      if (!shown && this.connected && !this.hasRenderedScene) {
        this.showInitialPlaceholder('unit_type_changed')
      }
    })
  }

  // The readiness strip describes a rendered scene; across a type switch that
  // scene no longer exists, so stale "Renders / Load planning ready" chips
  // must not outlive the canvas they described.
  clearReadiness() {
    if (this.hasStatusTarget) this.statusTarget.replaceChildren()
  }

  disconnectLivePreview() {
    window.clearTimeout(this.liveDebounceTimer)
    if (this.liveAbortController) this.liveAbortController.abort()
    this.clearFieldHighlights()

    if (this.liveFormElement) {
      if (this.handleLiveInput) this.liveFormElement.removeEventListener('input', this.handleLiveInput)
      if (this.handleLiveChange) this.liveFormElement.removeEventListener('change', this.handleLiveChange)
      if (this.handleMeasurementFocusIn) this.liveFormElement.removeEventListener('focusin', this.handleMeasurementFocusIn)
      if (this.handleMeasurementFocusOut) this.liveFormElement.removeEventListener('focusout', this.handleMeasurementFocusOut)
    }

    this.liveFormElement = null
    this.handleLiveInput = null
    this.handleLiveChange = null
    this.handleMeasurementFocusIn = null
    this.handleMeasurementFocusOut = null
    this.focusedMeasurementInput = null
    this.liveAbortController = null
  }

  async requestLivePreview() {
    if (!(this.livePreviewEnabled && this.liveFormElement)) return

    if (this.liveAbortController) this.liveAbortController.abort()
    const abortController = new AbortController()
    this.liveAbortController = abortController
    const sequence = (this.livePreviewSequence += 1)
    const body = new FormData(this.liveFormElement)
    // The preview endpoint is always POST. Edit forms carry Rails' hidden
    // `_method=put`, which would otherwise be interpreted by Rack before the
    // request reaches the preview action.
    body.delete('_method')

    let response
    try {
      response = await fetch(this.livePreviewUrl, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        signal: abortController.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
      })
    } catch (error) {
      if (error && error.name === 'AbortError') return
      this.handleLiveFailure(sequence)
      return
    }

    if (sequence !== this.livePreviewSequence || !this.connected) return

    if (!response.ok) {
      this.handleLiveFailure(sequence)
      return
    }

    let payload
    try {
      payload = await response.json()
    } catch (_error) {
      this.handleLiveFailure(sequence)
      return
    }

    if (sequence !== this.livePreviewSequence || !this.connected) return

    this.applyLiveResponse(payload, sequence)
  }

  async applyLiveResponse(payload, sequence) {
    if (!this.connected || (sequence !== undefined && sequence !== this.livePreviewSequence)) return
    // The axle strip listens for this to mirror server-derived values (e.g.
    // the locked front-jeep coupling gaps) back into the form display.
    this.element.dispatchEvent(new CustomEvent('tt:preview-scene', { detail: payload, bubbles: true }))

    const renderable = payload && payload.renderable === true
    const scene = payload && payload.render_scene
    const details = payload && Array.isArray(payload.render_reason_details)
      ? payload.render_reason_details
      : []

    // Every live response reconciles the field highlights: clear whatever was
    // marked last time, then re-mark only the currently offending inputs.
    this.clearFieldHighlights()

    if (renderable && scene) {
      const panel = await this.ensurePanel()
      if (!this.connected) return
      // The panel import above can suspend long enough for a newer response to
      // land; a stale scene must never overwrite it after the await.
      if (sequence !== undefined && sequence !== this.livePreviewSequence) return

      this.exampleRenderSequence += 1
      try {
        if (!panel) throw new Error('Preview unavailable')
        panel.update({
          trailer: scene,
          stretchMode: this.currentStretchMode,
          renderOptions: this.currentRenderOptions(),
          allowDefaultLegalEnvelope: this.allowDefaultLegalEnvelope,
        })
      } catch (_error) {
        this.handleLiveFailure(sequence)
        return
      }
      this.liveScene = scene
      this.hasRenderedScene = true
      this.exampleActive = false
      this.updateMeasurementCaption()
      this.setPaused(false)
      this.lastReadiness = payload.readiness || null
      this.renderReadiness(scene, { readiness: this.lastReadiness })
      this.syncStretchControlsWithForm()
      return
    }

    this.applyFieldHighlights(details)

    // Never destroy a scene that already rendered — pause instead. Dim the
    // stale frame so it visibly reads as not-current.
    if (this.hasRenderedScene) {
      this.setPaused(true)
      this.renderReadiness(this.lastRenderedScene, {
        readiness: this.lastReadiness,
        pausedReason: (payload && payload.render_reason) || 'render_unavailable',
        pausedDetails: details,
      })
      return
    }

    // Pre-first-render: keep (or take) the labeled family example on the
    // canvas instead of a text placeholder, and surface what's still missing
    // in the readiness strip.
    const reason = payload && payload.render_reason
    const notYetMessage =
      this.notRenderingYetMessage(reason, details) ||
      'Renders as you type — enter the key dimensions.'

    const showingExample = this.exampleActive || (await this.showFamilyExample())
    // showFamilyExample can wait for the renderer chunk. A newer response may
    // render a real scene while that happens, so the old response must not
    // append a contradictory "Not rendering yet" state after it resumes.
    if (!this.connected || (sequence !== undefined && sequence !== this.livePreviewSequence)) return

    if (showingExample) {
      this.showReadinessMessage(notYetMessage, 'warning')
      this.appendValidationMessages(this.statusTarget, details)
      return
    }

    this.showPlaceholder(
      this.notRenderingYetMessage(reason, details) ||
        'Renders as you type — choose a trailer type and enter the key dimensions.',
      '3D preview'
    )
  }

  // All errors appear below this heading and next to their inputs.
  notRenderingYetMessage(reason, details) {
    const messages = (details || [])
      .map(detail => (detail && typeof detail === 'object' ? stringValue(detail.message).trim() : ''))
      .filter(message => message.length > 0)

    if (messages.length > 0) {
      return 'Not rendering yet — correct the highlighted fields.'
    }

    const code = stringValue(reason)
    if (code.indexOf('invalid_') === 0) return `Not rendering yet — ${humanizeRenderReason(code)}.`

    return ''
  }

  fieldForDetail(detail) {
    if (!this.liveFormElement || !detail) return null
    const elements = Array.from(this.liveFormElement.elements).filter(input => !input.disabled)
    const named = name => elements.filter(input => input.name === name)
    if (detail.input_name) return named(detail.input_name)[detail.input_index || 0] || null

    const field = stringValue(detail.field).trim()
    if (!field) return null
    const units = Array.from(this.liveFormElement.querySelectorAll('[data-controller~="tractor-trailer-unit"]'))
    const unit = units[Number(detail.unit_position) - 1] ||
      units.find(candidate => candidate.dataset.unitKind === (detail.unit_kind || 'trailer'))
    if (field === 'axles_spacing') {
      const index = Number(detail.spacing_index) || 0
      const simplified = named(`chassis[${detail.unit_kind || 'trailer'}_axles_spacing][]`)
      if (simplified.length) return simplified[index] || null
      const unitIndex = units.indexOf(unit)
      const offset = units.slice(0, Math.max(0, unitIndex)).reduce((sum, preceding) => {
        const count = preceding.querySelector('[name="units[][number_of_axles]"]')
        return sum + Number(count && count.value || 0)
      }, 0)
      return named('chassis[axles_spacing][]')[offset + index] || null
    }
    const simplified = named(`tractor_trailer[${field}]`)[0]
    if (simplified) return simplified
    if (['well_deck_height', 'lower_deck_height'].includes(field)) {
      const mainDeck = named('tractor_trailer[deck_height]')[0]
      if (mainDeck) return mainDeck
    }
    const detailedField = ['tractor_length', 'trailer_length'].includes(field) ? 'length'
      : ['tractor_number_of_axles', 'front_helper_number_of_axles'].includes(field) ? 'number_of_axles'
        : field.replace(/^(jeep|booster)_/, '')
    const candidates = named(`units[][${detailedField}]`)
    const targetUnit = field === 'tractor_length' ? units.find(candidate => candidate.dataset.unitKind === 'tractor') : unit
    return candidates.find(input => targetUnit && targetUnit.contains(input)) || candidates[0] || null
  }

  applyFieldHighlights(details) {
    if (!this.liveFormElement || !Array.isArray(details)) return

    this.highlightedInputs = this.highlightedInputs || []

    const affectedFields = details.flatMap(detail => [detail,
      ...((detail && detail.related_fields) || []).map(field => ({ ...detail, field }))])
    affectedFields.forEach(detail => {
      const input = this.fieldForDetail(detail)
      if (!input || this.highlightedInputs.some(entry => entry.input === input)) return
      const group = input.closest('.form-group, .ttAxleStrip__gap, .ttAxleStrip__coupling, .ttAxleStrip__tw, .ttAxleStrip__helperMeta, .ttAxleStrip__cell')
      const error = document.createElement('div')
      this.fieldErrorSequence = (this.fieldErrorSequence || 0) + 1
      error.id = `tt-preview-error-${this.fieldErrorSequence}`
      error.className = 'ttPreviewFieldError'
      error.textContent = detailMessage(detail)
      this.highlightedInputs.push({ input, group, error,
        hadError: group && group.classList.contains('has-error'),
        invalid: input.getAttribute('aria-invalid'),
        describedBy: input.getAttribute('aria-describedby'),
      })
      input.classList.add('ttPreviewInvalid')
      input.setAttribute('aria-invalid', 'true')
      input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), error.id].filter(Boolean).join(' '))
      input.insertAdjacentElement('afterend', error)
      if (group) group.classList.add('has-error')
    })
  }

  clearFieldHighlights() {
    ;(this.highlightedInputs || []).forEach(({ input, group, error, hadError, invalid, describedBy }) => {
      input.classList.remove('ttPreviewInvalid')
      if (invalid === null) input.removeAttribute('aria-invalid')
      else input.setAttribute('aria-invalid', invalid)
      if (describedBy === null) input.removeAttribute('aria-describedby')
      else input.setAttribute('aria-describedby', describedBy)
      if (group && !hadError) group.classList.remove('has-error')
      error.remove()
    })
    this.highlightedInputs = []
  }

  appendValidationMessages(parent, details) {
    if (!parent || !details.length) return
    const list = document.createElement('ul')
    list.className = 'ttPreviewErrors'
    details.forEach(detail => {
      const message = detailMessage(detail)
      if (!message) return
      const item = document.createElement('li')
      const input = this.fieldForDetail(detail)
      if (input) {
        const link = document.createElement('button')
        link.type = 'button'
        link.textContent = message
        link.addEventListener('click', () => {
          input.scrollIntoView({ block: 'center', inline: 'center' })
          input.focus()
        })
        item.appendChild(link)
      } else item.textContent = message
      list.appendChild(item)
    })
    parent.appendChild(list)
  }

  handleLiveFailure(sequence) {
    if (sequence !== undefined && sequence !== this.livePreviewSequence) return
    this.setPaused(true)
    this.showReadinessMessage('Preview unavailable. Check the equipment dimensions and change a field to retry.', 'warning')
  }

  async ensurePanel() {
    if (this.panel) return this.panel
    if (this.panelCreationPromise) return this.panelCreationPromise

    const generation = this.panelCreationGeneration || 0
    const creationPromise = (async () => {
      try {
        const trailer3d = this.trailer3d || (await import(
          /* webpackChunkName: "tractor-trailer-3d" */ 'src/tractor_trailer_3d'
        ))
        if (!this.connected || generation !== this.panelCreationGeneration) return null

        this.trailer3d = trailer3d
        // Another caller may have supplied a panel while this import was
        // pending. Reuse it instead of allocating a second WebGL renderer.
        if (!this.panel) {
          this.panel = trailer3d.createPanel(this.containerTarget, {
            showStretchControls: false,
            stretchControlsEnabled: this.stretchControlsActive,
          })
        }
        return this.panel
      } catch (_error) {
        return null
      }
    })()

    this.panelCreationPromise = creationPromise
    try {
      return await creationPromise
    } finally {
      // destroyPanel may have invalidated this creation and installed a newer
      // promise; never let the stale continuation clear the replacement.
      if (this.panelCreationPromise === creationPromise) this.panelCreationPromise = null
    }
  }

  renderReadiness(scene, options = {}) {
    if (!this.hasStatusTarget) return

    this.lastRenderedScene = scene || this.lastRenderedScene
    const model = deriveReadiness(this.lastRenderedScene, options)

    const chipsRow = document.createElement('div')
    chipsRow.className = 'ttRenderReadinessChips'
    model.chips.forEach(chip => {
      const el = document.createElement('span')
      el.className = `ttRenderReadinessChip ttRenderReadinessChip--${chip.kind}`
      el.textContent = chip.label
      chipsRow.appendChild(el)
    })

    const fragment = document.createDocumentFragment()
    fragment.appendChild(chipsRow)
    if (model.planning) {
      const planningLine = document.createElement('div')
      planningLine.className = `ttRenderReadinessLine ttRenderReadinessLine--${model.planning.kind}`
      planningLine.textContent = model.planning.label
      fragment.appendChild(planningLine)
    }

    if (model.paused) {
      const pausedLine = document.createElement('div')
      pausedLine.className = 'ttRenderReadinessLine ttRenderReadinessLine--paused'
      pausedLine.textContent = model.paused.label
      fragment.appendChild(pausedLine)
      this.appendValidationMessages(fragment, options.pausedDetails && options.pausedDetails.length
        ? options.pausedDetails
        : model.paused.messages.map(message => ({ message })))
    }

    this.statusTarget.replaceChildren(fragment)
  }

  showReadinessMessage(message, kind = 'warning') {
    if (!this.hasStatusTarget) return

    const line = document.createElement('div')
    line.className = `ttRenderReadinessLine ttRenderReadinessLine--${kind === 'ready' ? 'ready' : 'paused'}`
    line.textContent = message

    line.setAttribute('data-live-message', 'true')
    this.statusTarget.replaceChildren(line)
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.getAttribute('content') : ''
  }

  connectLoadPreview() {
    this.selectedLoadIndex = 0
    this.renderTimer = null
    this.loadPreviewState = 'pending'
    this.loadPreviewError = null
    this.loadListExpanded = false
    this.formElement = this.element.closest('form')
    this.loadInputRoot = this.formElement || this.element
    this.editableLoadRows = this.normalizeLoadRows(this.initialLoadRows)
    this.editorHydrating = false
    this.handleLoadFieldChange = this.handleLoadFieldChange.bind(this)
    this.handleLoadSelectionClick = this.handleLoadSelectionClick.bind(this)

    this.loadInputRoot.addEventListener('input', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('keyup', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('change', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('click', this.handleLoadSelectionClick)

    this.observeExtraLoadRows()
    this.hydrateSelectedLoadEditor()
    this.mountLoadPreview()
    this.renderLoadPreview()
  }

  disconnectLoadPreview() {
    if (this.loadInputRoot) {
      this.loadInputRoot.removeEventListener('input', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('keyup', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('change', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('click', this.handleLoadSelectionClick)
    }
    if (this.extraLoadRowsObserver) this.extraLoadRowsObserver.disconnect()
    if (this.renderTimer) window.cancelAnimationFrame(this.renderTimer)

    this.destroyPanel()
    this.formElement = null
    this.loadInputRoot = null
    this.trailer3d = null
  }

  async mountLoadPreview() {
    try {
      const trailer3d = await import(
        /* webpackChunkName: "tractor-trailer-3d" */ 'src/tractor_trailer_3d'
      )
      if (!this.connected) return

      this.trailer3d = trailer3d
      this.panel = trailer3d.createPanel(this.containerTarget, {
        loadOnly: true,
        showStretchControls: false,
      })
      this.loadPreviewState = 'ready'
      this.renderLoadPreview()
    } catch (error) {
      this.loadPreviewState = 'unavailable'
      this.loadPreviewError = error instanceof Error ? error.message : 'render_mount_failed'
      this.showPlaceholder(this.loadPreviewError)
    }
  }

  observeExtraLoadRows() {
    if (typeof window === 'undefined' || !window.MutationObserver) return

    const container = this.extraLoadRowsContainer()
    if (!container) return

    this.extraLoadRowsObserver = new MutationObserver(() => this.scheduleLoadRender())
    this.extraLoadRowsObserver.observe(container, { childList: true, subtree: true })
  }

  handleLoadFieldChange(event) {
    const target = event.target
    if (!(target && target.matches && target.matches(LOAD_FIELD_SELECTOR))) return
    if (this.editorHydrating) return

    this.scheduleLoadRender()
  }

  handleLoadSelectionClick(event) {
    const toggle = event.target && event.target.closest
      ? event.target.closest(LOAD_LIST_TOGGLE_SELECTOR)
      : null
    if (toggle && this.element.contains(toggle)) {
      event.preventDefault()
      this.toggleLoadList()
      return
    }

    const row = event.target && event.target.closest
      ? event.target.closest('[data-load-index], [data-v2-load-index]')
      : null
    if (!row) return
    if (!(this.element.contains(row) || (this.formElement && this.formElement.contains(row)))) return
    if (this.selectedLoadEditorActive() && row.dataset.loadIndex === undefined) return

    const nextIndex = Number.parseInt(row.dataset.loadIndex || row.dataset.v2LoadIndex, 10)
    if (!Number.isInteger(nextIndex)) return

    if (row.dataset.loadIndex !== undefined) event.preventDefault()
    this.commitSelectedLoadEditor()
    this.selectedLoadIndex = nextIndex
    this.hydrateSelectedLoadEditor()
    this.renderLoadPreview()
  }

  toggleLoadList() {
    const rows = this.loadRowsForRender()
    const collapsedCount = this.collapsedLoadListCount()
    if (rows.length <= collapsedCount) return

    this.commitSelectedLoadEditor()
    this.loadListExpanded = !this.loadListExpanded
    if (!this.loadListExpanded && this.selectedLoadIndex >= collapsedCount) {
      this.selectedLoadIndex = 0
      this.hydrateSelectedLoadEditor()
      this.renderLoadPreview()
      return
    }

    this.syncLoadPreviewChrome(rows)
  }

  scheduleLoadRender() {
    if (this.renderTimer) window.cancelAnimationFrame(this.renderTimer)
    this.renderTimer = window.requestAnimationFrame(() => {
      this.renderTimer = null
      // The support-geometry form handler may auto-center the offset later in
      // the same input event. Commit on the next frame so selected multi-load
      // rows and the renderer both consume the normalized value.
      this.commitSelectedLoadEditor()
      this.renderLoadPreview()
    })
  }

  renderLoadPreview() {
    const rows = this.loadRowsForRender()
    const selectedRow = rows[this.safeLoadIndex(rows.length)]
    const loadVisual = selectedRow ? this.loadVisual(selectedRow) : null

    this.syncLoadPreviewChrome(rows)
    if (this.loadPreviewState === 'unavailable') {
      this.showPlaceholder(this.loadPreviewError || 'render_unavailable')
      return
    }

    if (!this.panel) {
      this.showPlaceholder('Loading preview...', 'Load preview')
      return
    }

    if (!loadVisual) {
      this.panel.clear()
      this.panel.showStatus('Enter L, W, and H')
      return
    }

    this.panel.update({
      loadVisuals: [loadVisual],
      renderOptions: DEFAULT_RENDER_OPTIONS,
    })
  }

  // Matrix review supplies the selected component through the same load-only
  // renderer as Quote V2, without rebuilding a WebGL panel for every edit.
  replaceLoadPreviewRows(event) {
    if (!this.loadPreview) return

    this.editableLoadRows = this.normalizeLoadRows(event.detail.rows || [])
    this.selectedLoadIndex = 0
    this.renderLoadPreview()
  }

  loadRowsForRender() {
    const initialRows = this.editableLoadRows || this.normalizeLoadRows(this.initialLoadRows)
    const formRows = this.normalizeLoadRows(this.currentFormLoadRows())
    const hasExtraRows = this.extraLoadRowElements().length > 0
    const hasEditablePrimaryLoad = this.primaryLoadFieldsPresent()

    if (initialRows.length > 1 && !hasExtraRows) return initialRows
    if (hasExtraRows || hasEditablePrimaryLoad) return formRows
    if (initialRows.length > 1) return initialRows
    if (formRows.length > 0) return formRows
    return initialRows
  }

  currentFormLoadRows() {
    const form = this.formElement
    if (!form) return []

    const rows = [
      {
        commodity_description: this.fieldValue(form, '#quote_commodity_description'),
        commodity_length: this.fieldValue(form, '#quote_commodity_length'),
        commodity_support_length: this.fieldValue(form, '#quote_commodity_support_length'),
        commodity_support_offset: this.fieldValue(form, '#quote_commodity_support_offset'),
        commodity_width: this.fieldValue(form, '#quote_commodity_width'),
        commodity_height: this.fieldValue(form, '#quote_commodity_height'),
        commodity_weight: this.fieldValue(form, '#quote_commodity_weight'),
      },
    ]

    this.extraLoadRowElements().forEach(rowElement => {
      const row = {}
      rowElement.querySelectorAll('[data-v2-load-extra-field]').forEach(field => {
        row[field.getAttribute('data-v2-load-extra-field')] = field.value || ''
      })
      rows.push(row)
    })

    return rows
  }

  extraLoadRowsContainer() {
    return this.formElement ? this.formElement.querySelector('[data-behavior~="v2-load-dimensions-extra-rows"]') : null
  }

  extraLoadRowElements() {
    const container = this.extraLoadRowsContainer()
    return container ? Array.from(container.querySelectorAll(EXTRA_LOAD_ROW_SELECTOR)) : []
  }

  primaryLoadFieldsPresent() {
    if (!this.formElement) return false

    return [
      '#quote_commodity_length',
      '#quote_commodity_width',
      '#quote_commodity_height',
      '#quote_commodity_weight',
    ].some(selector => this.formElement.querySelector(selector))
  }

  selectedLoadEditorActive() {
    return this.selectedLoadAssignmentField() && (this.editableLoadRows || []).length > 1
  }

  selectedLoadAssignmentField() {
    return this.formElement ? this.formElement.querySelector(SELECTED_LOAD_ASSIGNMENT_SELECTOR) : null
  }

  hydrateSelectedLoadEditor() {
    if (!this.selectedLoadEditorActive()) return

    const row = this.editableLoadRows[this.safeLoadIndex(this.editableLoadRows.length)]
    if (!row) return

    this.editorHydrating = true
    this.setFieldValue('#quote_commodity_description', row.commodity_description)
    this.setFieldValue('#quote_commodity_length', row.commodity_length)
    this.setFieldValue('#quote_commodity_support_length', row.commodity_support_length)
    this.setFieldValue('#quote_commodity_support_offset', row.commodity_support_offset)
    this.setFieldValue('#quote_commodity_width', row.commodity_width)
    this.setFieldValue('#quote_commodity_height', row.commodity_height)
    this.setFieldValue('#quote_commodity_weight', row.commodity_weight)
    this.syncPrimarySupportFootprintVisibility(row)
    const supportField = this.formElement.querySelector('#quote_commodity_support_length')
    const offsetField = this.formElement.querySelector('#quote_commodity_support_offset')
    if (supportField && offsetField && stringValue(supportField.value).trim() && !stringValue(offsetField.value).trim()) {
      supportField.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.syncSelectedLoadAssignmentField(row)
    this.editorHydrating = false
  }

  commitSelectedLoadEditor() {
    if (!this.selectedLoadEditorActive()) return

    const index = this.safeLoadIndex(this.editableLoadRows.length)
    const updatedRow = {
      ...this.editableLoadRows[index],
      ...this.currentPrimaryLoadRow(),
    }
    // Server rows include parsed numeric derivatives for initial rendering.
    // Once the user edits the source strings those cached values are stale and
    // must not override the live inputs (especially when support is cleared).
    ;[
      'commodity_length_in',
      'commodity_support_length_in',
      'commodity_support_offset_in',
      'commodity_width_in',
      'commodity_height_in',
      'commodity_weight_lb',
      'weight_lb',
      'support_length_in',
      'support_offset_in',
    ].forEach(key => delete updatedRow[key])

    this.editableLoadRows[index] = updatedRow
    this.syncSelectedLoadAssignmentField(updatedRow)
  }

  currentPrimaryLoadRow() {
    const form = this.formElement
    if (!form) return {}

    const supportOffsetField = form.querySelector('#quote_commodity_support_offset')
    const supportOffset = supportOffsetField ? supportOffsetField.value || '' : ''
    const supportOffsetMode = stringValue(
      supportOffsetField && supportOffsetField.dataset
        ? supportOffsetField.dataset.v2SupportOffsetMode
        : ''
    ).trim().toLowerCase()

    return {
      commodity_description: this.fieldValue(form, '#quote_commodity_description'),
      commodity_length: this.fieldValue(form, '#quote_commodity_length'),
      commodity_support_length: this.fieldValue(form, '#quote_commodity_support_length'),
      commodity_support_offset: supportOffset,
      commodity_support_offset_mode: supportOffsetMode === 'auto'
        ? 'auto'
        : (supportOffset.trim().length > 0 ? 'manual' : 'auto'),
      commodity_support_geometry: this.primarySupportFootprintVisible(),
      commodity_width: this.fieldValue(form, '#quote_commodity_width'),
      commodity_height: this.fieldValue(form, '#quote_commodity_height'),
      commodity_weight: this.fieldValue(form, '#quote_commodity_weight'),
    }
  }

  setFieldValue(selector, value) {
    if (!this.formElement) return

    const field = this.formElement.querySelector(selector)
    if (field) field.value = stringValue(value)
  }

  syncSelectedLoadAssignmentField(row) {
    const field = this.selectedLoadAssignmentField()
    if (!field) return

    field.value = stringValue(row.quote_load_assignment_id || row.id)
  }

  normalizeLoadRows(rows) {
    return (rows || [])
      .map((row, index) => {
        const source = row || {}
        return {
          id: source.id || `load-${index + 1}`,
          quote_load_assignment_id: stringValue(source.quote_load_assignment_id || source.assignment_id),
          label: source.label || `Load ${index + 1}`,
          commodity_description: stringValue(source.commodity_description || source.description),
          commodity_length: stringValue(source.commodity_length),
          commodity_support_length: stringValue(source.commodity_support_length),
          commodity_support_offset: stringValue(source.commodity_support_offset),
          commodity_support_offset_mode: this.supportOffsetMode(source),
          commodity_support_geometry: this.booleanValue(source.commodity_support_geometry),
          commodity_width: stringValue(source.commodity_width),
          commodity_height: stringValue(source.commodity_height),
          commodity_weight: stringValue(source.commodity_weight),
          weight_lb: this.numberOrNull(source.weight_lb || source.commodity_weight_lb),
          commodity_length_in: this.numberOrNull(source.commodity_length_in),
          commodity_support_length_in: this.numberOrNull(
            source.commodity_support_length_in || source.support_length_in
          ),
          commodity_support_offset_in: this.numberOrNull(
            source.commodity_support_offset_in == null
              ? source.support_offset_in
              : source.commodity_support_offset_in
          ),
          commodity_width_in: this.numberOrNull(source.commodity_width_in),
          commodity_height_in: this.numberOrNull(source.commodity_height_in),
        }
      })
      .filter(row => this.usefulLoadRow(row))
  }

  usefulLoadRow(row) {
    return [
      row.commodity_description,
      row.commodity_length,
      row.commodity_support_length,
      row.commodity_support_offset,
      row.commodity_width,
      row.commodity_height,
      row.commodity_weight,
      row.weight_lb,
      row.commodity_length_in,
      row.commodity_support_length_in,
      row.commodity_support_offset_in,
      row.commodity_width_in,
      row.commodity_height_in,
    ].some(value => value !== null && value !== undefined && String(value).trim().length > 0)
  }

  primarySupportFootprintRow() {
    return this.formElement
      ? this.formElement.querySelector('.v2-support-footprint-control[data-v2-load-index="0"]')
      : null
  }

  primarySupportFootprintVisible() {
    const row = this.primarySupportFootprintRow()
    const fields = row && row.querySelector('[data-behavior~="v2-support-footprint-fields"]')
    return Boolean(fields && !fields.classList.contains('hidden'))
  }

  syncPrimarySupportFootprintVisibility(row) {
    const primaryRow = this.primarySupportFootprintRow()
    if (!primaryRow) return

    const fields = primaryRow.querySelector('[data-behavior~="v2-support-footprint-fields"]')
    const offsetField = primaryRow.querySelector('[data-v2-load-field="commodity_support_offset"]')
    const active = this.booleanValue(row && row.commodity_support_geometry) ||
      stringValue(row && row.commodity_support_length).trim().length > 0 ||
      stringValue(row && row.commodity_support_offset).trim().length > 0
    if (fields) fields.classList.toggle('hidden', !active)
    if (offsetField) {
      const offsetValue = stringValue(row && row.commodity_support_offset)
      const offsetMode = this.supportOffsetMode(row)
      offsetField.dataset.v2SupportOffsetMode = offsetMode
      offsetField.dataset.v2SupportAutoOffsetValue = offsetMode === 'auto' ? offsetValue : ''
    }
  }

  supportOffsetMode(row) {
    const explicitMode = stringValue(
      row && (row.commodity_support_offset_mode || row.support_offset_mode)
    ).trim().toLowerCase()
    if (explicitMode === 'auto' || explicitMode === 'manual') return explicitMode

    const offsetValue = row && row.commodity_support_offset != null
      ? row.commodity_support_offset
      : row && row.support_offset
    return stringValue(offsetValue).trim().length > 0 ? 'manual' : 'auto'
  }

  booleanValue(value) {
    if (value === true) return true

    const normalized = stringValue(value).trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }

  safeLoadIndex(rowCount) {
    if (rowCount <= 0) {
      this.selectedLoadIndex = 0
      return 0
    }

    if (this.selectedLoadIndex < 0 || this.selectedLoadIndex >= rowCount) this.selectedLoadIndex = 0
    return this.selectedLoadIndex
  }

  syncLoadPreviewChrome(rows) {
    this.element.classList.toggle('is-multi-load', rows.length > 1)
    const layout = this.element.closest('.quote-v2-load-dimensions-grid')
    if (layout) layout.classList.toggle('quote-v2-load-dimensions-grid--multi', rows.length > 1)
    const collapsedCount = this.collapsedLoadListCount()
    const hasOverflowLoads = rows.length > collapsedCount
    if (!hasOverflowLoads) this.loadListExpanded = false
    this.element.classList.toggle('is-load-list-expanded', hasOverflowLoads && this.loadListExpanded)

    this.loadRowTargets.forEach(row => {
      const rowIndex = Number.parseInt(row.dataset.loadIndex, 10)
      row.classList.toggle('is-selected', rowIndex === this.selectedLoadIndex)
    })

    this.loadListToggleTargets.forEach(button => {
      const expanded = hasOverflowLoads && this.loadListExpanded
      button.hidden = !hasOverflowLoads
      button.setAttribute('aria-expanded', String(expanded))
      button.textContent = expanded
        ? button.dataset.expandedLabel || 'Show fewer loads'
        : button.dataset.collapsedLabel || 'Show more loads'
    })
  }

  collapsedLoadListCount() {
    const count = Number.parseInt(this.element.dataset.loadListCollapsedCount, 10)
    return Number.isInteger(count) && count > 0 ? count : 5
  }

  loadVisual(row) {
    const lengthIn = this.dimensionInches(row, 'commodity_length')
    const widthIn = this.dimensionInches(row, 'commodity_width')
    const heightIn = this.dimensionInches(row, 'commodity_height')
    if (!(lengthIn && widthIn && heightIn)) return null
    const weightLb = this.weightPounds(row)
    const enteredSupportLengthIn = this.dimensionInches(
      row,
      'commodity_support_length',
      'commodity_length'
    )
    const supportLengthIn = enteredSupportLengthIn && enteredSupportLengthIn <= lengthIn
      ? enteredSupportLengthIn
      : null
    const enteredSupportOffsetIn = this.dimensionInches(
      row,
      'commodity_support_offset',
      'commodity_support_offset',
      { allowZero: true }
    )
    const supportOffsetIn = supportLengthIn
      ? (enteredSupportOffsetIn == null ? (lengthIn - supportLengthIn) / 2 : enteredSupportOffsetIn)
      : null
    const supportGeometryValid = supportLengthIn && supportOffsetIn >= 0 &&
      supportOffsetIn + supportLengthIn <= lengthIn + 0.001
    const x0In = -lengthIn / 2

    return {
      x0_in: x0In,
      x1_in: lengthIn / 2,
      bottom_in: 0,
      top_in: heightIn,
      length_in: lengthIn,
      width_in: widthIn,
      height_in: heightIn,
      centerZ_in: 0,
      weight_lb: weightLb,
      previewOnly: false,
      ...(supportGeometryValid
        ? {
            support_x0_in: x0In + supportOffsetIn,
            support_x1_in: x0In + supportOffsetIn + supportLengthIn,
            ...(supportLengthIn < lengthIn - 0.001
              ? {
                  body_bottom_in: Math.min(
                    LOAD_ONLY_SUPPORT_PEDESTAL_MAX_IN,
                    heightIn * LOAD_ONLY_SUPPORT_PEDESTAL_HEIGHT_RATIO
                  ),
                }
              : {}),
          }
        : {}),
    }
  }

  weightPounds(row) {
    const explicitValue = this.numberOrNull(row.weight_lb)
    if (explicitValue > 0) return explicitValue

    return parseLiveWeightPounds(row.commodity_weight)
  }

  dimensionInches(row, key, parserKey = key, options = {}) {
    const explicitValue = this.numberOrNull(row[`${key}_in`])
    if (explicitValue > 0 || (options.allowZero && explicitValue === 0)) return explicitValue

    const parsedValue = parseLiveDimensionInches(row[key], parserKey, options)
    if (parsedValue > 0 || (options.allowZero && parsedValue === 0)) return parsedValue

    return null
  }

  fieldValue(root, selector) {
    const field = root.querySelector(selector)
    return field ? field.value || '' : ''
  }

  numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null

    const number = Number(value)
    return Number.isFinite(number) ? number : null
  }

  bindFormStretchControls() {
    if (!this.showStretchControls) return

    this.formElement = this.element.closest('form')
    if (!this.formElement) return

    this.handleFormStretchControlChange = event => {
      const target = event.target
      if (!(target && target.matches)) return
      if (!target.matches('[data-behavior~="stretch-enabled"], [data-behavior~="unit-type-select"]')) return

      window.setTimeout(() => this.syncStretchControlsWithForm(), 0)
    }

    this.formElement.addEventListener('change', this.handleFormStretchControlChange)
  }

  bindRenderStretchControls() {
    if (!this.hasStretchControlTarget) return

    this.handleRenderStretchControlClick = event => {
      const button = event.target && event.target.closest
        ? event.target.closest('[data-stretch-mode]')
        : null
      if (!(button && this.stretchControlTarget.contains(button))) return

      event.preventDefault()
      this.setStretchMode(button.dataset.stretchMode)
    }

    this.stretchControlTarget.addEventListener('click', this.handleRenderStretchControlClick)
  }

  unbindRenderStretchControls() {
    if (!(this.hasStretchControlTarget && this.handleRenderStretchControlClick)) return

    this.stretchControlTarget.removeEventListener('click', this.handleRenderStretchControlClick)
    this.handleRenderStretchControlClick = null
  }

  unbindFormStretchControls() {
    if (!(this.formElement && this.handleFormStretchControlChange)) return

    this.formElement.removeEventListener('change', this.handleFormStretchControlChange)
    this.formElement = null
    this.handleFormStretchControlChange = null
  }

  syncStretchControlsWithForm() {
    const enabled = this.stretchControlsActive && this.renderSceneStretchCapable

    if (!enabled) {
      this.currentStretchMode = 'closed'
    }

    if (this.panel && typeof this.panel.setStretchControlsEnabled === 'function') {
      this.panel.setStretchControlsEnabled(enabled)
    }

    this.syncStretchControlDisplay(enabled)
  }

  setStretchMode(stretchMode) {
    if (!this.stretchControlsActive) return

    this.currentStretchMode = stretchMode === 'extended' ? 'extended' : 'closed'

    if (this.panel && typeof this.panel.setStretchMode === 'function') {
      this.panel.setStretchMode(this.currentStretchMode)
    }

    this.syncStretchControlDisplay(this.renderSceneStretchCapable)
  }

  syncStretchControlDisplay(enabled) {
    if (!this.hasStretchControlTarget) return

    this.stretchControlTarget.hidden = !(this.showStretchControls && enabled)

    const buttons = this.stretchControlTarget.querySelectorAll('[data-stretch-mode]')
    buttons.forEach(button => {
      const active = button.dataset.stretchMode === this.currentStretchMode
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
    })

    if (this.hasStretchMetaTarget) {
      const extraIn = this.renderSceneStretchExtraIn
      this.stretchMetaTarget.textContent = extraIn > 0 ? `+${this.formatFeetInches(extraIn)}` : ''
    }
  }

  formatFeetInches(valueIn) {
    return this.trailer3d && typeof this.trailer3d.formatFeetInches === 'function'
      ? this.trailer3d.formatFeetInches(valueIn)
      : ''
  }

  get renderPayload() {
    if (this._renderPayload !== undefined) return this._renderPayload

    try {
      this._renderPayload = JSON.parse(this.payloadTarget.textContent)
    } catch (_error) {
      this._renderPayload = null
    }

    return this._renderPayload
  }

  get familyExampleScenes() {
    if (this._familyExampleScenes !== undefined) return this._familyExampleScenes

    if (!this.hasFamilyExamplesTarget) {
      this._familyExampleScenes = null
      return null
    }

    try {
      this._familyExampleScenes = JSON.parse(this.familyExamplesTarget.textContent)
    } catch (_error) {
      this._familyExampleScenes = null
    }

    return this._familyExampleScenes
  }

  get annotationSettings() {
    const raw = stringValue(this.element.dataset.annotationSettings).trim()
    if (!raw) return null

    try {
      return this.normalizeAnnotationSettings(JSON.parse(raw))
    } catch (_error) {
      return null
    }
  }

  get annotationSettingsUrl() {
    const url = stringValue(this.element.dataset.annotationSettingsUrl).trim()
    return url || null
  }

  get initialLoadRows() {
    const payload = this.renderPayload
    return payload && Array.isArray(payload.load_rows) ? payload.load_rows : []
  }

  get loadPreview() {
    return this.element.dataset.loadPreview === 'true'
  }

  get livePreviewUrl() {
    const url = stringValue(this.element.dataset.livePreviewUrl).trim()
    return url || null
  }

  get livePreviewEnabled() {
    return !this.loadPreview && this.livePreviewUrl !== null
  }

  get allowDefaultLegalEnvelope() {
    return this.element.dataset.allowDefaultLegalEnvelope === 'true'
  }

  get showStretchControls() {
    return this.element.dataset.showStretchControls === 'true'
  }

  get renderSceneStretch() {
    // Live-preview scenes supersede the initial script-tag payload; without
    // this the stretch controls keep reading the page-load scene forever.
    const scene = this.liveScene || (this.renderPayload && this.renderPayload.render_scene)
    return (scene && scene.stretch) || {}
  }

  get renderSceneStretchExtraIn() {
    const stretch = this.renderSceneStretch
    const extra = Number(stretch.max_extra_in || stretch.maxExtra_in)
    return Number.isFinite(extra) && extra > 0 ? extra : 0
  }

  get renderSceneStretchCapable() {
    const stretch = this.renderSceneStretch
    return this.renderSceneStretchExtraIn > 0 && (
      stretch.enabled === true ||
      stretch.enabled === 'true' ||
      Array.isArray(stretch.stretch_surface_ids) ||
      Array.isArray(stretch.stretchSurfaceIds)
    )
  }

  get stretchControlsActive() {
    if (!this.showStretchControls) return false

    const form = this.formElement || this.element.closest('form')
    if (!form) return true

    return Array.from(form.querySelectorAll('[data-behavior~="stretch-enabled"]')).some(input => {
      const wrapper = input.closest('[data-geometry-stretch-field]')
      return input.checked && !(wrapper && wrapper.classList.contains('hidden'))
    })
  }
}
